import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { currentTimeZone } from '../../../models/timeZones/timeZones.model';
import { ICryptoService } from '../../../services/crypto.service';
import { IEventBus } from '../../../services/eventBus.service';
import { IUserStore } from '../../user/user.store';
import { CreateWorkspaceButton } from '../components/CreateWorkspaceButton';
import { CreateWorkspaceModal } from '../components/CreateWorkspaceModal';
import { KeyDownloadModal } from '../components/KeyDownloadModal';
import { WorkspaceCreatedEvent } from '../events/workspaceCreatedEvent';
import { IWorkspaceService } from '../workspaces.service';

export const CreateWorkspaceContainer = observer((): ReactElement => {
  const workspaceService = useInjection(IWorkspaceService.$);
  const cryptoService = useInjection(ICryptoService.$);
  const userStore = useInjection(IUserStore.$);
  const eventBus = useInjection(IEventBus.$);

  const { isLoading, withLoaderAsync } = useLoader();
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceInputStatus, setNewWorkspaceInputStatus] = useState(InputStatus.empty());
  const [newWorkspaceModalShown, setNewWorkspaceModalShown] = useState(false);
  const [newWorkspaceEncryption, setNewWorkspaceEncryption] = useState(false);
  const [keyDownloadModalShown, setKeyDownloadModalShown] = useState(false);
  const [encryptionKeyUrl, setEncryptionKeyUrl] = useState<string | undefined>(undefined);
  const [newWorkspaceId, setNewWorkspaceId] = useState<string | undefined>(undefined);
  const [newWorkspaceTimeZone, setNewWorkspaceTimeZone] = useState(currentTimeZone ? currentTimeZone.value : 0);

  const toggleCreateWorkspaceModal = (): void => {
    if (isLoading) {
      return;
    }
    setNewWorkspaceModalShown(!newWorkspaceModalShown);
  };

  const toggleKeyDownload = (): void => {
    setKeyDownloadModalShown(!keyDownloadModalShown);
    if (newWorkspaceId) {
      notifyThatWorkspaceWasCreated(newWorkspaceId);
    }
  };

  const handleNewWorkspaceNameChange = (value: string): void => {
    setNewWorkspaceName(value);
    setNewWorkspaceInputStatus(validateWorkspaceName(value));
  };

  const handleEncryptionChange = (): void => {
    setNewWorkspaceEncryption(!newWorkspaceEncryption);
  };

  const handleTimeZoneChange = (newWorkspaceTimeZone: number): void => {
    setNewWorkspaceTimeZone(newWorkspaceTimeZone);
  };

  const handleSubmitCreate = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async (): Promise<void> => {
      if (!newWorkspaceInputStatus.isValid) {
        return;
      }
      const result = await workspaceService.createWorkspaceAsync(newWorkspaceName, newWorkspaceEncryption, newWorkspaceTimeZone);

      if (result instanceof ACDLError) {
        if (result.isBadRequestWithCode(['CAN_NOT_CREATE_MORE_WORKSPACES'])) {
          workspaceService.store.canAddWorkspace = false;
          return;
        }

        setNewWorkspaceInputStatus(InputStatus.buildFrom(result.apiErrorResponse?.errorCodes ?? []));
      } else {
        setNewWorkspaceInputStatus(InputStatus.empty());
        setNewWorkspaceModalShown(false);
        setNewWorkspaceId(result);

        if (newWorkspaceEncryption && result) {
          const encryptionKeyUrl = await cryptoService.exportKey(result);
          setEncryptionKeyUrl(encryptionKeyUrl);
          setKeyDownloadModalShown(true);
        } else {
          if (result) {
            notifyThatWorkspaceWasCreated(result);
          }
        }
      }
    }, 'create-workspace-button');
  };

  const validateWorkspaceName = (value: string): InputStatus => {
    const errorCodes: TranslationKey[] = [];

    if (value.trim() === '') {
      errorCodes.push('field_cant_be_empty');
    }

    const isNotUnique = workspaceService.store.workspaces.map((w) => w.name.toLocaleUpperCase()).includes(value.trim().toLocaleUpperCase());

    if (isNotUnique) {
      errorCodes.push('DUPLICATE_WORKSPACE_NAME');
    }

    return InputStatus.buildFrom(errorCodes);
  };

  const notifyThatWorkspaceWasCreated = (workspaceId: string): void => {
    eventBus.sendEvent(new WorkspaceCreatedEvent(workspaceId));
  };

  const { plan } = userStore.planInfo;

  return (
    <>
      <CreateWorkspaceButton onCreateButtonClicked={toggleCreateWorkspaceModal} />
      <CreateWorkspaceModal
        plan={plan}
        showCreateWorkspace={newWorkspaceModalShown}
        newWorkspaceName={newWorkspaceName}
        newWorkspaceNameStatus={newWorkspaceInputStatus}
        newWorkspaceEncryption={newWorkspaceEncryption}
        newWorkspaceTimeZone={newWorkspaceTimeZone}
        onCloseModal={toggleCreateWorkspaceModal}
        onCreateNameChange={handleNewWorkspaceNameChange}
        onEncryptionChange={handleEncryptionChange}
        onTimeZoneChanged={handleTimeZoneChange}
        onSubmitCreate={handleSubmitCreate}
        canAddWorkspace={workspaceService.store.canAddWorkspace}
      />
      <KeyDownloadModal
        newWorkspaceName={newWorkspaceName}
        showKeyDownload={keyDownloadModalShown}
        encryptionKeyUrl={encryptionKeyUrl || ''}
        toggleKeyDownload={toggleKeyDownload}
      />
    </>
  );
});
